/**
 * Store product extensions (admin side).
 *
 * The product dialog is the CMS editor of the StoreProduct type: its tabs are
 * the type's groups. An extension contributes a group and the fields that go in
 * it, so a project composes the product model instead of living with one fixed
 * dialog. The server half declares the same id under
 * `components/<component>/store/extensions/<id>` and owns the schema fields;
 * `store {product_model}` says which ids are enabled, in which order.
 *
 * An extension is a folder with an index.js exporting:
 *
 *   export const StoreProductExtension = {
 *     id: "organization",                       // matches the server extension
 *     label: "Organization",
 *     description: "...",                       // shown in the settings tab
 *     group: { id, label, description },        // the dialog tab (id defaults to the extension id)
 *     icon: TagIcon,                            // the tab's icon (a section may carry its own)
 *     fields: [ { id, label, type, ... } ],     // CMS fields, group filled in automatically
 *   }
 *
 * A tab that CMS fields can't express (a page editor, a key importer) declares
 * a section instead of fields — a component that owns the whole tab:
 *
 *     section: { component, label, icon, order } // order places the tab (default 1000)
 *     top: true                                  // optional: the tab opens the dialog
 *     provider: Component                        // optional, see below
 *     visible: (ctx) => boolean                  // optional, see below
 *     visibilityContext: Hook                    // optional, see below
 *     actions: [Component]                       // optional, see below
 *     subtitle: Component                        // optional, shown under the dialog's title
 *
 * The section component is rendered by the CMS content editor with
 * { group, product, dataItem, contentType, value, onChange, language, register }.
 * `register({ dirty, save })` joins the dialog's Save: a section that reports
 * itself dirty has its `save()` awaited before the fields are written, and a
 * save that returns false stops the whole save. A save may instead answer with
 * the store fields it wrote (`{ stock: 10 }`): the form takes them before it
 * saves, so it does not write back the values it loaded.
 *
 * Sections that share one editor state declare the same `provider` component
 * (compared by identity, so it mounts once): it wraps the whole editor — above
 * the tab switch, so its state survives a tab change — and is rendered with
 * { dataItem, contentType, language, register, children }.
 *
 * A tab a given product has no use for is left out of the dialog altogether:
 * `visible(ctx)` is asked before the tab strip is built and answers false to
 * drop the group. `ctx` is { product, dataItem, contentType, language } plus
 * whatever the enabled `visibilityContext` hooks add — the rules run above the
 * editor, outside the providers, so anything they need (a record the product
 * points at, a permission) is loaded by such a hook. Each hook is called once
 * per render with the context built so far and merged into it; they are
 * deduplicated by identity, like providers, and the list is fixed while a
 * model is applied, so calling them in order keeps React's hook order.
 *
 * Which tabs a product has is first the product's content type's: the type
 * lists its extensions (`ProductContentType.extensions`, edited with the
 * StoreExtensionsEditor field editor), and a product's resolved list is the
 * enabled ids ∩ that list — an empty or missing list means every enabled one
 * (`resolveStoreProductExtensions`). The model applies it to every tab, the
 * framework's and the project's alike, so no extension needs a `visible` of
 * its own for it. The list reaches the rules as `storeExtensions` in `ctx`:
 * the `visibilityContext` hook that knows the product's content type answers
 * it (the host resolves it from however its products name their type). While no
 * hook answers it, or the type's list is empty, the tabs fall back to their
 * own `visible` rules alone, as before the types had a list. An extension's
 * own `visible` still applies on top (a permission, a record the tab needs);
 * a rule that only stands in for the content type defers to the list once the
 * list is there.
 *
 * Pricing and Availability are every product's, so an extension that takes
 * one of them over (`replaces: ["pricing"]`) would leave a product without it
 * wherever its type's list does not name it. Such an extension declares
 * `allProducts: true`: the content type's list does not scope it, and its own
 * `visible` alone decides (a permission — who edits the price this way).
 *
 * The built-in tabs follow the same context: a `visibilityContext` hook may
 * answer `hiddenBuiltinTabs: [id]` — the built-in tabs this editor does not
 * show (a project hiding Metadata from who may not edit it); see builtin-tabs.js.
 *
 * `subtitle` is what the extension shows under the dialog's title — a state
 * badge, a key, whatever names the record rather than acting on it. It renders
 * inside the providers, like the actions, and is called with
 * { dataItem, contentType, values, language }.
 *
 * `actions` are the components the editor's header renders next to Save — the
 * product-wide affordances that are not a tab (a status switch, a delete).
 * They render inside the providers, so a section's shared editor is theirs
 * too, and they are called with
 * { dataItem, contentType, values, onChange, language, close }.
 *
 * An extension declares fields or a section, not both; a section wins.
 *
 * Fields that belong in a tab another one owns declare where they go instead
 * of a group of their own:
 *
 *     extends: ["product-info", "details"]       // the first tab that is there
 *
 * — an enabled extension's tab, or a built-in one (details, pricing,
 * availability, licensing, metadata); the editor renders them under what that
 * tab shows — in the first of the listed tabs the product has. They follow
 * the extension's own `visible` and the content type's list like a tab would
 * (`field.extendsTabs`, `field.extensionVisible`).
 *
 * The framework's own extensions live here; a project keeps its own in
 * /components/Store/extensions/<id>/index.{js,jsx}.
 */
import { StoreProduct } from "aldoo-ra/CMSModels/StoreProduct"

const extensions = new Map()

// What an extension contributed carries its id, on the group and on every
// field: applying a model again finds its own leftovers on the type itself
// rather than in module state, which a hot reload of this module would lose
// while the type — a module that did not change — kept the groups.
const strip = () => {
  StoreProduct.groups = StoreProduct.groups.filter((group) => !group.extension)
  StoreProduct.fields = StoreProduct.fields.filter((field) => !field.extension)
}

/** Declare an extension (called by its module when it loads). */
export const registerStoreProductExtension = (definition) => {
  if (!definition?.id) throw new Error("registerStoreProductExtension: an id is required")
  extensions.set(definition.id, definition)
  return definition
}

export const getStoreProductExtension = (id) => extensions.get(id) || null
export const getStoreProductExtensions = () => [...extensions.values()]

const groupOf = (extension) => ({
  id: extension.group?.id || extension.id,
  label: extension.group?.label || extension.label,
  description: extension.group?.description || extension.description || "",
  ...(extension.group || {}),
})

const sectionOf = (extension) => (extension.section?.component ? extension.section : null)

// the product editor's own tabs, which an extension's fields may extend
export const BUILTIN_STORE_PRODUCT_TABS = ["details", "pricing", "availability", "licensing", "metadata"]

const extendsOf = (extension) => {
  const target = extension.extends || extension.section?.extends
  return Array.isArray(target) ? target : target ? [target] : []
}

/**
 * A product's extensions: the enabled ids (in their order) that its content
 * type lists; an empty or missing list means all of them. The storefront and
 * the server answer the same (aldoo-na/store StoreExtensions.forProduct).
 */
import { OPTIONAL_BUILTIN_TAB_IDS } from "./builtin-tabs"
export { OPTIONAL_BUILTIN_TABS, OPTIONAL_BUILTIN_TAB_IDS, builtinTabVisible } from "./builtin-tabs"

const extensionIdsOf = (list) => (Array.isArray(list) ? list.filter((id) => !OPTIONAL_BUILTIN_TAB_IDS.includes(id)) : [])

export const resolveStoreProductExtensions = (enabledIds = [], contentTypeExtensions) => {
  const listed = extensionIdsOf(contentTypeExtensions)
  if (!listed.length) return [...enabledIds]
  return enabledIds.filter((id) => listed.includes(id))
}

/** The content type's list of extensions as the visibility rules see it, or null when it names none. */
export const storeExtensionsScope = (context) => {
  const listed = extensionIdsOf(context?.storeExtensions)
  return listed.length ? listed : null
}


/**
 * The tab's rule: in the product's resolved list when its content type has
 * one, and the extension's own rule (if any) on top. An extension that stands
 * in for a tab every product has (`allProducts`, e.g. one that replaces
 * Pricing) is not the content type's to leave out: its own rule alone decides.
 */
const scopedVisible = (id, own, { allProducts = false } = {}) => (context) => {
  const scope = allProducts ? null : storeExtensionsScope(context)
  if (scope && !scope.includes(id)) return false
  return own ? own(context) !== false : true
}

/**
 * The values the enabled extensions contribute under `key`, each one only once.
 * Identity is the comparison: extensions that share a provider, a visibility
 * hook or a header action declare the very same component.
 */
const collect = (enabledIds, key) => {
  const values = []
  for (const id of enabledIds) {
    const value = extensions.get(id)?.[key]
    for (const entry of Array.isArray(value) ? value : value ? [value] : []) {
      if (values.includes(entry)) continue
      values.push(entry)
    }
  }
  return values
}

/** The shared providers of the enabled extensions, each one only once. */
export const getStoreProductSectionProviders = (enabledIds = []) => collect(enabledIds, "provider")

/**
 * Compose the StoreProduct type: the framework groups and fields, plus the
 * enabled extensions. A section's `order` places its tab; everything else keeps
 * the enabled order. Safe to call again — what a previous call added is removed
 * first, so with nothing enabled the type is the framework's own again.
 */
export const applyStoreProductModel = (enabledIds = []) => {
  strip()
  const applied = []
  delete StoreProduct.sectionProviders
  delete StoreProduct.visibilityContexts
  delete StoreProduct.actions
  delete StoreProduct.subtitles

  const enabled = enabledIds
    .map((id, index) => ({ extension: extensions.get(id), index }))
    .filter((entry) => entry.extension)
    // an unordered extension keeps its place after the ordered ones
    .sort((a, b) => (a.extension.section?.order ?? 1000 + a.index) - (b.extension.section?.order ?? 1000 + b.index))

  // fields that go into another tab are placed once every tab is known
  const extenders = []
  for (const { extension } of enabled) {
    if (!sectionOf(extension) && extendsOf(extension).length) {
      extenders.push(extension)
      continue
    }
    const section = sectionOf(extension)
    const group = groupOf(extension)
    if (section) {
      group.label = section.label || group.label
      group.component = section.component
    }
    // the tab's icon: the group's own, the section's, or the extension's — a
    // fields-only extension has no section to hold one
    group.icon = group.icon || section?.icon || extension.icon
    // a mark beside the tab's name (a component, rendered inside the editor's providers: it may read the
    // product — a count of what waits there, say)
    if (section?.badge) group.badge = section.badge
    // where its tab stands among all of them, the framework's own included (the editor sorts by it)
    if (Number.isFinite(section?.order)) group.order = section.order
    // the tab may rule itself out for a product; the editor asks before it
    // builds the tab strip. The content type's extensions list rules first
    const visible = extension.visible || section?.visible
    group.visible = scopedVisible(extension.id, visible, { allProducts: !!extension.allProducts })
    // the editor tells an extension's tab from the framework's own by this
    group.extension = extension.id
    // a tab that opens the dialog whenever it is enabled, before the framework's
    // own and before the extensions that replace them
    if (extension.top ?? section?.top) group.top = true
    // an extension may take over a built-in tab it makes redundant: the editor
    // drops that tab while this one is enabled and visible. `absorbs` takes it
    // over too, but renders what that tab held inside this one — two halves of
    // the same subject, one tab
    const replaces = extension.replaces || section?.replaces
    if (replaces) group.replaces = Array.isArray(replaces) ? replaces : [replaces]
    const absorbs = extension.absorbs || section?.absorbs
    if (absorbs) group.absorbs = Array.isArray(absorbs) ? absorbs : [absorbs]
    // the fields other extensions put into this tab (`extends`) are laid out by the
    // section itself, in its own grid, instead of under it (it gets them as `extendedFields`)
    if (extension.placesExtendedFields ?? section?.placesExtendedFields) group.placesExtendedFields = true
    // a section owns its tab: its fields, if any, are the server's business
    const fields = section
      ? []
      : (extension.fields || []).map((field) => ({
          ...field,
          group: field.group || group.id,
          extension: extension.id,
        }))

    StoreProduct.groups = [...StoreProduct.groups, group]
    StoreProduct.fields = [...StoreProduct.fields, ...fields]
    applied.push({ groupId: group.id, fieldIds: fields.map((field) => field.id) })
  }

  for (const extension of extenders) {
    const tabs = [...StoreProduct.groups.filter((group) => group.extension).map((group) => group.id), ...BUILTIN_STORE_PRODUCT_TABS]
    // every listed tab that is there: the editor renders the fields in the
    // first of them a product shows (Information, or Details where it has none)
    const targets = extendsOf(extension).filter((id) => tabs.includes(id))
    if (!targets.length) continue
    const fields = (extension.fields || []).map((field) => ({
      ...field,
      group: targets[0],
      extension: extension.id,
      extendsTab: targets[0],
      extendsTabs: targets,
      extensionVisible: scopedVisible(extension.id, extension.visible, { allProducts: !!extension.allProducts }),
    }))
    StoreProduct.fields = [...StoreProduct.fields, ...fields]
  }

  const enabledOrder = enabled.map(({ extension }) => extension.id)
  const providers = getStoreProductSectionProviders(enabledOrder)
  if (providers.length) StoreProduct.sectionProviders = providers
  const visibilityContexts = collect(enabledOrder, "visibilityContext")
  if (visibilityContexts.length) StoreProduct.visibilityContexts = visibilityContexts
  const actions = collect(enabledOrder, "actions")
  if (actions.length) StoreProduct.actions = actions
  const subtitles = collect(enabledOrder, "subtitle")
  if (subtitles.length) StoreProduct.subtitles = subtitles

  return applied.map((entry) => entry.groupId)
}

// The framework's own extensions, and the ones the project keeps in
// /components/Store/extensions — the glob resolves at build time and is empty
// when the folder is absent.
const modules = {
  ...import.meta.glob("./*/index.{js,jsx}", { eager: true }),
  ...import.meta.glob("/components/Store/extensions/*/index.{js,jsx}", { eager: true }),
}

for (const [path, module] of Object.entries(modules)) {
  const definition = module.StoreProductExtension || module.default
  if (!definition) continue
  registerStoreProductExtension({ id: path.split("/").at(-2), ...definition })
}
